import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

export default function SofixOverlayStory({ guide }) {
  const location = useLocation();
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [activeSpotlightIndex, setActiveSpotlightIndex] = useState(0);
  const [debugOverrides, setDebugOverrides] = useState({});
  const maskId = useId();
  const stepRefs = useRef([]);
  const isDebugMode = useMemo(() => new URLSearchParams(location.search).get('debug') === '1', [location.search]);

  const steps = useMemo(() => {
    if (!guide?.steps?.length || !guide?.pages?.length) return [];
    return guide.steps.map((step, index) => ({
      ...step,
      index,
      page: guide.pages[step.pageIndex],
    }));
  }, [guide]);

  const resolvedSteps = useMemo(() => {
    return steps.map((step, index) => {
      const override = debugOverrides[index];
      if (!override) return step;
      return { ...step, ...override };
    });
  }, [debugOverrides, steps]);

  useEffect(() => {
    setActiveSpotlightIndex(0);
  }, [activeStepIndex]);

  if (!resolvedSteps.length) return null;

  const activeStep = resolvedSteps[Math.min(activeStepIndex, resolvedSteps.length - 1)];
  const spotlights = activeStep?.spotlights?.length
    ? activeStep.spotlights
    : activeStep?.spotlight
      ? [activeStep.spotlight]
      : [];
  const primarySpotlight = spotlights[0];
  const spotlightCenterX = primarySpotlight ? (primarySpotlight.x + primarySpotlight.w / 2) * 100 : 50;
  const spotlightCenterY = primarySpotlight ? (primarySpotlight.y + primarySpotlight.h / 2) * 100 : 50;
  const zoom = activeStep?.zoom || primarySpotlight?.zoom || 1.18;
  const translateX = 50 - ((primarySpotlight?.x || 0) + (primarySpotlight?.w || 0) / 2) * 100 * zoom;
  const translateY = 50 - ((primarySpotlight?.y || 0) + (primarySpotlight?.h || 0) / 2) * 100 * zoom;

  function getCornerRadius(spotlight) {
    return Math.min((spotlight.radius || 20) / 8, 6);
  }

  useEffect(() => {
    const activeNode = stepRefs.current[activeStepIndex];
    if (!activeNode) return;
    activeNode.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [activeStepIndex]);

  function updateActiveSpotlight(field, value) {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return;
    setDebugOverrides((current) => {
      const stepOverride = current[activeStepIndex] || {};
      const baseSpotlights = (stepOverride.spotlights || spotlights).map((spotlight) => ({ ...spotlight }));
      if (!baseSpotlights[activeSpotlightIndex]) return current;
      baseSpotlights[activeSpotlightIndex][field] = numeric;
      return {
        ...current,
        [activeStepIndex]: {
          ...stepOverride,
          spotlights: baseSpotlights,
        },
      };
    });
  }

  function updateStepZoom(value) {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return;
    setDebugOverrides((current) => {
      const stepOverride = current[activeStepIndex] || {};
      return {
        ...current,
        [activeStepIndex]: {
          ...stepOverride,
          zoom: numeric,
        },
      };
    });
  }

  const currentSpotlight = spotlights[activeSpotlightIndex] || spotlights[0];
  const currentDebugOutput = {
    step: activeStepIndex + 1,
    pageIndex: activeStep.pageIndex,
    zoom,
    spotlights,
  };
  const captionStyle = activeStep.captionPosition === 'top'
    ? { top: '18px', bottom: 'auto' }
    : undefined;

  return (
    <section className="sofix-story">
      <div className="sofix-story-visual">
        <div className="sofix-story-sticky">
          <div className="sofix-story-frame">
            <div className="sofix-story-image-stage">
              <img
                src={activeStep.page?.image}
                alt={activeStep.page?.label || 'SOFIX検査結果'}
                className="sofix-story-image"
                loading="lazy"
                style={{
                  transformOrigin: '0 0',
                  transform: `translate(${translateX}%, ${translateY}%) scale(${zoom})`,
                }}
              />
            </div>
            <svg
              className="sofix-story-overlay"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <mask id={maskId}>
                  <rect width="100" height="100" fill="white" />
                  {spotlights.map((spotlight, index) => (
                    <rect
                      key={`${activeStep.id || activeStep.title}-mask-${index}`}
                      x={spotlight.x * 100}
                      y={spotlight.y * 100}
                      width={spotlight.w * 100}
                      height={spotlight.h * 100}
                      rx={getCornerRadius(spotlight)}
                      ry={getCornerRadius(spotlight)}
                      fill="black"
                    />
                  ))}
                </mask>
              </defs>
              <rect
                width="100"
                height="100"
                fill="rgba(8, 12, 11, 0.56)"
                mask={`url(#${maskId})`}
              />
              {spotlights.map((spotlight, index) => (
                <rect
                  key={`${activeStep.id || activeStep.title}-outline-${index}`}
                  x={spotlight.x * 100}
                  y={spotlight.y * 100}
                  width={spotlight.w * 100}
                  height={spotlight.h * 100}
                  rx={getCornerRadius(spotlight)}
                  ry={getCornerRadius(spotlight)}
                  fill="none"
                  stroke="rgba(255,255,255,0.92)"
                  strokeWidth="0.22"
                />
              ))}
            </svg>
            <div className="sofix-story-caption" style={captionStyle}>
              <p className="number-label">{activeStep.page?.label}</p>
              <h3>{activeStep.title}</h3>
            </div>
          </div>
        </div>
      </div>

      <div className="sofix-story-steps">
        {guide.intro ? <p className="sofix-story-intro">{guide.intro}</p> : null}
        <div className="sofix-story-step-list">
          {resolvedSteps.map((step, index) => (
            <article
              key={step.id || `${step.pageIndex}-${index}`}
              ref={(node) => {
                stepRefs.current[index] = node;
              }}
              className={`sofix-story-step ${activeStepIndex === index ? 'is-active' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => setActiveStepIndex(index)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setActiveStepIndex(index);
                }
              }}
            >
              <p className="number-label">STEP {String(index + 1).padStart(2, '0')}</p>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </article>
          ))}
        </div>
        {isDebugMode ? (
          <div className="sofix-debug-panel">
            <div className="sofix-debug-head">
              <strong>デバッグモード</strong>
              <span className="meta-text">?debug=1</span>
            </div>
            {spotlights.length > 1 ? (
              <div className="sofix-debug-spotlights">
                {spotlights.map((_, index) => (
                  <button
                    key={`spot-${index}`}
                    type="button"
                    className={`tab-btn ${activeSpotlightIndex === index ? 'is-active' : ''}`}
                    onClick={() => setActiveSpotlightIndex(index)}
                  >
                    枠 {index + 1}
                  </button>
                ))}
              </div>
            ) : null}
            <label className="sofix-debug-field">
              <span>zoom</span>
              <input type="range" min="1" max="2.4" step="0.01" value={zoom} onChange={(e) => updateStepZoom(e.target.value)} />
              <input type="number" min="1" max="2.4" step="0.01" value={zoom} onChange={(e) => updateStepZoom(e.target.value)} />
            </label>
            {currentSpotlight ? (
              <>
                <label className="sofix-debug-field">
                  <span>x</span>
                  <input type="range" min="0" max="1" step="0.001" value={currentSpotlight.x} onChange={(e) => updateActiveSpotlight('x', e.target.value)} />
                  <input type="number" min="0" max="1" step="0.001" value={currentSpotlight.x} onChange={(e) => updateActiveSpotlight('x', e.target.value)} />
                </label>
                <label className="sofix-debug-field">
                  <span>y</span>
                  <input type="range" min="0" max="1" step="0.001" value={currentSpotlight.y} onChange={(e) => updateActiveSpotlight('y', e.target.value)} />
                  <input type="number" min="0" max="1" step="0.001" value={currentSpotlight.y} onChange={(e) => updateActiveSpotlight('y', e.target.value)} />
                </label>
                <label className="sofix-debug-field">
                  <span>w</span>
                  <input type="range" min="0.02" max="1" step="0.001" value={currentSpotlight.w} onChange={(e) => updateActiveSpotlight('w', e.target.value)} />
                  <input type="number" min="0.02" max="1" step="0.001" value={currentSpotlight.w} onChange={(e) => updateActiveSpotlight('w', e.target.value)} />
                </label>
                <label className="sofix-debug-field">
                  <span>h</span>
                  <input type="range" min="0.02" max="1" step="0.001" value={currentSpotlight.h} onChange={(e) => updateActiveSpotlight('h', e.target.value)} />
                  <input type="number" min="0.02" max="1" step="0.001" value={currentSpotlight.h} onChange={(e) => updateActiveSpotlight('h', e.target.value)} />
                </label>
              </>
            ) : null}
            <pre className="sofix-debug-output">{JSON.stringify(currentDebugOutput, null, 2)}</pre>
          </div>
        ) : null}
      </div>
    </section>
  );
}
